import { useEffect } from 'react';

import { Accordion } from '@chakra-ui/react';
import { IInvoiceTemplateConfigSchema } from '@monetize/types/app';
import { useForm } from 'react-hook-form';
import {
  useGetInvoiceSettings,
  useUpdateInvoiceSettings,
} from '../../../../api/settingsService';
import {
  MAccordionCustomButton,
  MButton,
  MFlex,
  MPageContainer,
  MPageLoader,
  MSettingsPageHeader,
  MStack,
} from '../../../../components/Monetize';
import { MSettingAccordionItem } from '../../../../components/Monetize/MSettingAccordionItem';
import { SETTINGS_CONTENT_MAX_WIDTH } from '../../../../constants/settings';
import {
  getHighlightStyles,
  useSettingsSectionHighlight,
} from '../../../../hooks/useSettingsSectionHighlight';
import { useDocumentHead } from '../../../../services/documentHead';
import { InvoiceTemplateForm } from './components/InvoiceTemplateForm';

const InvoiceTemplates = () => {
  const { setDocTitle } = useDocumentHead();
  const isHighlighted = useSettingsSectionHighlight('invoiceTemplates');
  const { data: invoiceSettings, isLoading: isDataLoading } =
    useGetInvoiceSettings();
  const { mutate: doUpdateInvoiceSettings, isPending: isUpdateLoading } =
    useUpdateInvoiceSettings();

  const {
    handleSubmit,
    control,
    formState: { isDirty, errors },
    reset,
  } = useForm<IInvoiceTemplateConfigSchema>({
    mode: 'onChange',
  });

  useEffect(() => {
    if (invoiceSettings) {
      reset({ ...invoiceSettings.invoiceTemplateConfig });
    }
  }, [invoiceSettings]);

  useEffect(() => {
    setDocTitle('Settings', 'Invoice Templates');
  }, []);

  const onSubmit = (payload: IInvoiceTemplateConfigSchema) => {
    invoiceSettings &&
      doUpdateInvoiceSettings({
        ...invoiceSettings,
        invoiceTemplateConfig: payload,
      });
  };

  return (
    <MPageContainer data-testid="settings-invoice-templates">
      <MSettingsPageHeader title="Invoice Templates" />
      <MStack w="100%" overflow="auto" p={1} data-testid="InvoiceTemplates">
        {isDataLoading ? (
          <MPageLoader maxW={SETTINGS_CONTENT_MAX_WIDTH} height="450px" />
        ) : (
          <Accordion.Root
            multiple
            maxW={SETTINGS_CONTENT_MAX_WIDTH}
            data-testid="qTemplate-accordion"
            defaultValue={['0']}
          >
            <MSettingAccordionItem
              value="0"
              id="invoiceTemplates"
              renderAccordionButton={({
                isExpanded,
              }: {
                isExpanded: boolean;
              }) => (
                <MAccordionCustomButton
                  isExpanded={isExpanded}
                  label="Invoice Templates"
                  data-testid="invoice-template-mAccordionBtn"
                />
              )}
              description="Configure the default settings for Invoices"
              {...getHighlightStyles(isHighlighted)}
              borderWidth="1px"
            >
              <InvoiceTemplateForm
                errors={errors}
                control={control}
                handleSubmit={handleSubmit(onSubmit)}
              />

              <MFlex justifyContent="flex-end">
                <MButton
                  type="submit"
                  data-testid="submit-invoiceTemplate-form"
                  variant="primary"
                  mt={4}
                  alignSelf="self-end"
                  isDisabled={isUpdateLoading || !isDirty}
                  isLoading={isUpdateLoading}
                  onClick={handleSubmit(onSubmit)}
                >
                  Save
                </MButton>
              </MFlex>
            </MSettingAccordionItem>
          </Accordion.Root>
        )}
      </MStack>
    </MPageContainer>
  );
};

export { InvoiceTemplates };
